/*
 * Shared SEO constants and schema.org builders.
 *
 * SITE_URL is the canonical host. It must match the host Vercel actually serves
 * (apex; www 308s to it). Change this one constant and every canonical, og:url,
 * sitemap <loc> and JSON-LD url follows.
 */
export const SITE_URL = 'https://horsestat.com';
export const APP_URL = 'https://app.horsestat.com';
export const SITE_NAME = 'HorseStat';
export const CONTACT_EMAIL = 'contact@horsestat.com';

/* One-sentence definition used verbatim on the home pages, About and llms.txt. */
export const DEFINITION =
  'HorseStat is a data-driven horse racing analysis platform for UK and Irish racing that puts every runner\u2019s form, pace, going, class, trainer, jockey, owner, sire and dam statistics into a single interactive racecard, alongside a proprietary HorseStat Rating built from 10,000 simulations of every race.';

export const absoluteUrl = (path = '/') => `${SITE_URL}${path === '/' ? '/' : path.replace(/\/$/, '')}`;

/* Content review date for help articles without their own `updated` field. */
export const CONTENT_REVIEWED = '2026-09-25';

export const PLAN_OFFERS = [
  { name: 'Free', price: '0', description: 'Every UK & Irish race with core racecard data.' },
  { name: 'Weekender (48-hour pass)', price: '10.00', description: 'Full HorseStat Pro access for 48 hours.' },
  { name: 'HorseStat Pro', price: '14.99', description: 'Unlimited daily access to all 22 analysis tables.', billing: 'P1M' },
];

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: 'HORSESTAT LTD',
  url: SITE_URL,
  foundingDate: '2025-12',
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/logo512.png`,
    width: 512,
    height: 512,
  },
  description: DEFINITION,
  email: CONTACT_EMAIL,
  contactPoint: {
    '@type': 'ContactPoint',
    email: CONTACT_EMAIL,
    contactType: 'customer support',
    availableLanguage: 'English',
  },
  areaServed: ['GB', 'IE'],
  identifier: {
    '@type': 'PropertyValue',
    name: 'Companies House',
    value: '17179664',
  },
  sameAs: [
    'https://find-and-update.company-information.service.gov.uk/company/17179664',
    APP_URL,
  ],
});

export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  publisher: { '@id': `${SITE_URL}/#organization` },
  inLanguage: 'en-GB',
});

export const softwareApplicationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  '@id': `${SITE_URL}/#app`,
  name: SITE_NAME,
  url: APP_URL,
  description: DEFINITION,
  applicationCategory: 'SportsApplication',
  applicationSubCategory: 'Horse racing analysis',
  operatingSystem: 'Web, iOS, Android',
  browserRequirements: 'Requires JavaScript',
  inLanguage: 'en-GB',
  publisher: { '@id': `${SITE_URL}/#organization` },
  featureList: [
    'Interactive racecards for every UK & Irish race',
    'HorseStat Rating: predicted finishing position from 10,000 simulations across 22 variables',
    'Complete form history for every runner',
    '22 analysis tables: performance, going, class, distance, pace map, draw bias',
    'Trainer, jockey, owner, sire and dam statistics',
    'Runner highlights and race filtering',
  ],
  offers: PLAN_OFFERS.map((plan) => ({
    '@type': 'Offer',
    name: plan.name,
    price: plan.price,
    priceCurrency: 'GBP',
    description: plan.description,
    url: `${SITE_URL}/pricing`,
    availability: 'https://schema.org/InStock',
    ...(plan.billing
      ? {
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: plan.price,
            priceCurrency: 'GBP',
            billingDuration: 1,
            billingIncrement: 1,
            unitCode: 'MON',
          },
        }
      : {}),
  })),
});

export const faqSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export const articleSchema = ({ title, description, path, body, updated, category }) => ({
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: title,
  description,
  articleBody: body,
  articleSection: category,
  url: absoluteUrl(path),
  mainEntityOfPage: absoluteUrl(path),
  inLanguage: 'en-GB',
  datePublished: updated || CONTENT_REVIEWED,
  dateModified: updated || CONTENT_REVIEWED,
  author: { '@id': `${SITE_URL}/#organization` },
  publisher: { '@id': `${SITE_URL}/#organization` },
  isPartOf: { '@id': `${SITE_URL}/#website` },
});

/* Home FAQ: short, direct answers that answer engines can quote. */
export const HOME_FAQ = [
  {
    question: 'What is HorseStat?',
    answer: DEFINITION,
  },
  {
    question: 'What is the HorseStat Rating?',
    answer:
      'The HorseStat Rating is a predictive rating for every runner. Each race is simulated 10,000 times across 22 variables (form, class, distance, going, pace, draw, trainer, jockey and more) and the rating reflects the horse\u2019s predicted finishing position. Runners on the racecard are ranked by it.',
  },
  {
    question: 'Which races does HorseStat cover?',
    answer:
      'Every UK and Irish race, flat and jumps, including all-weather meetings. Racecards are available for the current and upcoming days, and results-day cards stay available for review.',
  },
  {
    question: 'Is HorseStat free?',
    answer:
      'Yes. The Free plan includes every UK and Irish racecard with core data, official ratings and the HorseStat Rating. HorseStat Pro (\u00a314.99 a month) or a 48-hour Weekender pass (\u00a310) unlock all 22 analysis tables, form history, pace maps, draw bias and connection statistics.',
  },
  {
    question: 'Is there a HorseStat app?',
    answer:
      'HorseStat runs in any browser at app.horsestat.com and as a mobile app for iPhone and Android with the same racecards, analysis tables and ratings.',
  },
  {
    question: 'Does HorseStat give tips?',
    answer:
      'No. HorseStat is an analysis tool, not a tipping service. It organises the data behind every race so you can form your own view faster and with more context.',
  },
];
